/**
 * Client portal look (2026-09-06) — the billing app's own palette and square, compact style, so the
 * portal reads as the same product (client: "ensure the styling aligns with the original").
 * Values are copied from `C` / CATEGORY_STYLE / MATCH_FILTER_* in src/jf_medical_billing.jsx — keep
 * them in sync when those change.
 */
export const C = {
  bg: "#F8F9FF",
  bgCard: "#FFFFFF",
  border: "#C4C6CF",
  text: "#0B1C30",
  textMid: "#43474E",
  textLight: "#74777F",
  navy: "#000613",
  sidebarBrand: "#001a2c",
  sidebarBrandDeep: "#001428",
  amber: "#F59E0B",
  amberLight: "#FEF3C7",
  green: "#10B981",
  success: "#10B981",
  successBg: "#D1FAE5",
  warning: "#d97706",
  warningBg: "#FEF3C7",
  danger: "#EF4444",
  dangerBg: "#FEE2E2",
  teal: "#006972",
  tealLight: "#EFF4FF",
  tealDark: "#004F56",
  slate: "#6B7280",
  slateBg: "#F3F4F6",
  purple: "#8B5CF6",
};

/** Status pill colors — same as the billing app's CATEGORY_STYLE (Open is yellow). */
/**
 * Keep in sync with CATEGORY_STYLE in src/jf_medical_billing.jsx. Paid / Open / Loss / Patient
 * Responsibility / RECOUP carry the client's MASTER BE SHEET legend hex in `fill` (2026-09-06).
 */
export const STATUS_STYLE = {
  paid:        { bg: "#EAF6DB", color: "#4F7A20", label: "Paid", fill: "#92D050" },
  partial:     { bg: "#DBEAFE", color: "#001F3F", label: "Partial Payment" },
  pending:     { bg: "#FEF3C7", color: "#d97706", label: "Pending" },
  open:        { bg: "#FFF59D", color: "#7A5C00", label: "Open", fill: "#FFFF00" },
  recoup:      { bg: "#DCF2EC", color: "#058067", label: "RECOUP", fill: "#058067" },
  duplicate_primary: { bg: "#FEF3C7", color: "#B45309", label: "DUPLICATE PRIMARY $" },
  duplicate_2nd:   { bg: "#FEF9C3", color: "#A16207", label: "DUPLICATE 2ND $" },
  review:      { bg: "#DBEAFE", color: "#1D4ED8", label: "REVIEW" },
  reprocess:   { bg: "#D1FAE5", color: "#047857", label: "REPROCESS" },
  ppwk_request:{ bg: "#CFFAFE", color: "#0E7490", label: "PPWK Request" },
  monitor:     { bg: "#CCFBF1", color: "#0F766E", label: "Monitor" },
  hold:        { bg: "#FFF0CC", color: "#8A6600", label: "Hold", fill: "#FFC000" },
  hospital:    { bg: "#E8EDF8", color: "#3B5A9B", label: "Hospital", fill: "#8EA9DB" },
  timely_filing:{ bg: "#ECFCCB", color: "#4D7C0F", label: "Timely Filing" },
  overdue:     { bg: "#FEE2E2", color: "#EF4444", label: "Overdue" },
  hidden:      { bg: "#F3F4F6", color: "#74777F", label: "Hidden" },
  loss:        { bg: "#F0E6F7", color: "#7030A0", label: "Loss", fill: "#7030A0" },
  patient_resp:{ bg: "#FDE7F2", color: "#F21D76", label: "Patient Responsibility", fill: "#F21D76" },
  unequal:     { bg: "#FFEDD5", color: "#C2410C", label: "Unequal" },
  Secondary:   { bg: "#F3F4F6", color: "#6B7280", label: "Secondary" },
  denied:      { bg: "#FEE2E2", color: "#B91C1C", label: "Denied", fill: "#B91C1C" }, // 2026-10-02
  appeal:      { bg: "#FEF3C7", color: "#F59E0B", label: "In Appeal" },
  collections: { bg: "#ffedd5", color: "#ea580c", label: "Collections" },
  null:        { bg: "#F3F4F6", color: "#74777F", label: "Null" },
  excluded:    { bg: "#F3F4F6", color: "#43474E", label: "Excluded" },
  "":          { bg: "#F3F4F6", color: "#74777F", label: "Uncategorized" },
};
/**
 * Colours and labels the biller set in the staff app's Identifiers tab, delivered by
 * GET /api/portal/schedule. Merged over the built-ins so a created status is never grey.
 */
const CUSTOM_STATUS_STYLE = {};
function pillFrom(hex, label) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || "").trim());
  if (!m) return null;
  const r = parseInt(m[1].slice(0, 2), 16), g = parseInt(m[1].slice(2, 4), 16), b = parseInt(m[1].slice(4, 6), 16);
  const lin = (c) => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  const light = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b) > 0.5;
  const mix = (c) => Math.round(c + (255 - c) * 0.86);
  const dark = (c) => Math.round(c * (light ? 0.45 : 0.82));
  const hx = (c) => c.toString(16).padStart(2, "0");
  return { bg: `#${hx(mix(r))}${hx(mix(g))}${hx(mix(b))}`, color: `#${hx(dark(r))}${hx(dark(g))}${hx(dark(b))}`, fill: `#${m[1].toLowerCase()}`, label };
}
export function registerStatusStyles(colors, labels) {
  for (const k of Object.keys(CUSTOM_STATUS_STYLE)) delete CUSTOM_STATUS_STYLE[k];
  for (const [value, hex] of Object.entries(colors || {})) {
    const label = (labels && labels[value]) || (STATUS_STYLE[value] || {}).label || value;
    const pill = pillFrom(hex, label);
    if (pill) CUSTOM_STATUS_STYLE[value] = pill;
  }
  for (const [value, label] of Object.entries(labels || {})) {
    if (!STATUS_STYLE[value] && !CUSTOM_STATUS_STYLE[value]) CUSTOM_STATUS_STYLE[value] = { bg: "#F3F4F6", color: "#43474E", label };
  }
}
export function statusStyle(value) {
  const k = value == null ? "" : String(value);
  return CUSTOM_STATUS_STYLE[k] || STATUS_STYLE[k] || STATUS_STYLE[""];
}

export const FILTER_LABEL = { display: "flex", flexDirection: "column", gap: 4, minWidth: 0 };
export const FILTER_CAPTION = { fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.4, color: C.textLight, whiteSpace: "nowrap" };
export const FILTER_CONTROL = { width: "100%", height: 32, boxSizing: "border-box", background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: "0 8px", fontSize: 12, color: C.text, outline: "none" };
export const COUNT_TAG = { display: "inline-flex", alignItems: "center", gap: 4, height: 24, padding: "0 8px", borderRadius: 4, border: `1px solid ${C.border}`, background: C.bg, fontSize: 11, color: C.textMid, whiteSpace: "nowrap" };
export const CARD = { background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" };
export const SQUARE_BTN = (on) => ({ height: 30, padding: "0 12px", background: on ? C.navy : C.bgCard, border: `1px solid ${on ? C.navy : C.border}`, borderRadius: 4, color: on ? "#fff" : C.textMid, cursor: "pointer", fontSize: 12, fontWeight: on ? 800 : 600 });
export const STICKY_TH = { position: "sticky", top: 0, zIndex: 2, background: C.navy, color: "#fff", padding: "7px 10px", fontSize: 10, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", textAlign: "left", whiteSpace: "nowrap" };
export const NUM_TD = { padding: "5px 8px", fontSize: 12, textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", color: C.text };

export function fmt(n) {
  const v = Number(n) || 0;
  const abs = Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `-$${abs}` : `$${abs}`;
}

/** Same page-title style as the dashboard cards (bigger, heavier, navy bar on the left). */
export const SECTION_TITLE = { display: "inline-flex", alignItems: "center", fontWeight: 900, fontSize: 15, lineHeight: 1.15, color: C.navy, textTransform: "uppercase", letterSpacing: "0.05em", borderLeft: `4px solid ${C.navy}`, paddingLeft: 10 };

export const MAIN_APP_URL = "https://jfprofessionalsolutions.net/";
export const PORTAL_URL = "https://portals.jfprofessionalsolutions.net/";
